import axios from 'axios';
import { DirectUpload } from '@rails/activestorage';

// Drop-in replacement for @rails/activestorage's DirectUpload that recovers from a stale CSRF token.
//
// Devise rotates the session's CSRF token at every sign-in. A tab that was open before a re-login in another tab
// still carries the old token in its meta tag, so its next direct upload is refused. ActiveStorage::BaseController
// answers that refusal with a 422 JSON body carrying a `code` (see REFUSAL_CODES) and an `error` message, plus a
// fresh `csrf_token` and the `user_id` the session now belongs to when the page may retry. This class writes the
// fresh token into the page and retries the upload once, but only when that user is the one this page was rendered
// for, so a document started by one person is never saved under another's sign-in: a refusal naming a different user
// is not retried. A session signed out since the page loaded (`signed_out`), a timed-out session (`session_ended`)
// and another origin (`origin_refused`) carry no token at all, so there is nothing to retry with, whatever the page
// decides about the user id. Whenever the refusal carries a message the callback gets
// it instead of the library's "Error creating Blob ... Status: 422". Every request is marked X-Direct-Upload-Wrapper
// (and the retry X-Direct-Upload-Retry) so the server reports only the refusals nothing here recovers from.
export default class DirectUploadWithRetry {
  constructor(file, url, delegate) {
    this.file = file;
    this.url = url;
    this.delegate = delegate;
  }

  create(callback) {
    this.attempt(callback, true);
  }

  attempt(callback, retryAllowed) {
    this.blobRequest = null;
    this.retrying = !retryAllowed;
    const upload = new DirectUpload(this.file, this.url, this);

    upload.create((error, blob) => {
      if (!error) {
        callback(null, blob);
        return;
      }

      const refusal = refusalResponse(this.blobRequest);
      if (refusal && refusal.csrf_token && retryAllowed && sameUserAsPage(refusal.user_id)) {
        refreshCsrfToken(refusal.csrf_token);
        this.attempt(callback, false);
        return;
      }

      callback(refusal ? refusal.error : error);
    });
  }

  // DirectUpload delegate callbacks. The blob request is kept so its response can be read on failure; both are
  // passed through to the caller's delegate so progress handling keeps working. The retry is marked with a header
  // so the server stays quiet on the expected first refusal but still reports one that survives the fresh token.
  directUploadWillCreateBlobWithXHR(request) {
    this.blobRequest = request;
    // Marks the request as the wrapper's, so the server can tell a refusal this page will recover from (not reported)
    // from one sent by a caller that cannot retry, such as an ActionText attachment (reported).
    request.setRequestHeader('X-Direct-Upload-Wrapper', '1');
    if (this.retrying) request.setRequestHeader('X-Direct-Upload-Retry', '1');
    notify(this.delegate, 'directUploadWillCreateBlobWithXHR', request);
  }

  directUploadWillStoreFileWithXHR(request) {
    notify(this.delegate, 'directUploadWillStoreFileWithXHR', request);
  }
}

// The `code` values ActiveStorage::BaseController sends with a refusal. A 422 body without one (for example the
// `{ status, error }` JSON the app's error pages render) is some other failure and keeps the library's message.
const REFUSAL_CODES = ['stale_token', 'signed_out', 'session_ended', 'origin_refused'];

// Returns the parsed body when ActiveStorage::BaseController refused the blob request: `{ code, error, csrf_token,
// user_id }` when the page may retry, or `{ code, error }` when there is nothing to retry with (the session has timed
// out, or the request came from another origin). Null for any other failure, whose library message is kept. The
// library opens the blob request with responseType = 'json', so `response` is the parsed body (null when it was not
// JSON) and `responseText` would throw.
export function refusalResponse(request) {
  if (!request || request.status !== 422) return null;

  const body = request.response;
  if (!body || typeof body !== 'object') return null;
  if (!REFUSAL_CODES.includes(body.code)) return null;
  return typeof body.error === 'string' && body.error !== '' ? body : null;
}

// True when the session that refused the upload belongs to the user this page was rendered for. Pages without
// the meta (signed-out layouts) have no user to compare, so they retry.
export function sameUserAsPage(userId) {
  const meta = document.querySelector('meta[name="current-user-id"]');
  if (!meta) return true;

  return String(meta.getAttribute('content')) === String(userId);
}

// Writes a fresh token everywhere the page reads it: the meta tag that XHRs (including DirectUpload) use, the
// axios default header that lib/axios.ts copied from the meta tag at page load, and the hidden field of every
// form on the page, so the request that follows the upload is accepted too.
export function refreshCsrfToken(token) {
  if (!token) return;

  const meta = document.querySelector('meta[name="csrf-token"]');
  if (meta) meta.setAttribute('content', token);

  if (axios.defaults.headers.common['X-CSRF-Token']) {
    axios.defaults.headers.common['X-CSRF-Token'] = token;
  }

  document.querySelectorAll('input[name="authenticity_token"]').forEach((input) => {
    input.value = token;
  });
}

function notify(object, methodName, ...args) {
  if (object && typeof object[methodName] === 'function') {
    object[methodName](...args);
  }
}
